import type { BankAccountType } from "@/apis/gql/types";
import { type ApolloError, gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { BANK_ACCOUNTS_QUERY } from "./get-all";

type CreateBankAccountInput = {
  name: string;
  type: BankAccountType;
};

type CreateBankAccountResponse = {
  createBankAccount: {
    bankAccountId: string;
    name: string;
    type: BankAccountType;
    defaultAccount: boolean;
  };
};

export const useCreateBankAccount = (): [
  (
    data: CreateBankAccountInput,
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<CreateBankAccountResponse["createBankAccount"]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();

  const [mutate, { loading, error }] = useMutation<CreateBankAccountResponse>(
    CREATE_BANK_ACCOUNT_MUTATION,
    {
      refetchQueries: [BANK_ACCOUNTS_QUERY],
      awaitRefetchQueries: true,
    },
  );

  const handleCreate = async (
    data: CreateBankAccountInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));
    try {
      const res = await mutate({ variables: data });
      if (!res.data) {
        throw new Error("No data");
      }
      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return res.data.createBankAccount;
    } catch (e) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw e;
    }
  };

  return [handleCreate, { loading, error }];
};

export const CREATE_BANK_ACCOUNT_MUTATION = gql`
  mutation CreateBankAccount($name: String!, $type: String!) {
    createBankAccount(name: $name, type: $type) {
      bankAccountId
      name
      type
      defaultAccount
    }
  }
`;
